import App from '@/App';
import { NavigateWithUrlSearchParams } from '@/components/NavigateWithUrlSearchParams';
import Account from '@/routes/account';
import Tickets from '@/routes/account/tickets/Tickets';
import TravelPasses from '@/routes/account/travelPasses/TravelPasses';
import Login from '@/routes/login';
import TaraCallback from '@/routes/login/TaraCallback';
import { BookingDetails } from '@/routes/manage/BookingDetails';
import BookingSearch from '@/routes/manage/BookingSearch';
import EditPassengerDetails from '@/routes/manage/EditPassengerDetails';
import { ManageBookingLayout } from '@/routes/manage/ManageBookingLayout';
import Purchase from '@/routes/purchase';
import Checkout from '@/routes/purchase/Checkout';
import InboundJourneyList from '@/routes/purchase/InboundJourneyList';
import InboundJourneyOfferList from '@/routes/purchase/InboundJourneyOfferList';
import NonTripOfferList from '@/routes/purchase/NonTripOfferList';
import OutboundJourneyList from '@/routes/purchase/OutboundJourneyList';
import OutboundJourneyOfferList from '@/routes/purchase/OutboundJourneyOfferList';
import NewPasswordFormWithGuard from '@/routes/reset-password/NewPasswordFormWithGuard';
import PasswordResetLinkForm from '@/routes/reset-password/PasswordResetLinkForm';
import SignUp from '@/routes/signup';
import SignupConfirmation from '@/routes/signup/SignupConfirmation';
import SignupVerify from '@/routes/signup/SignupVerify';
import Success from '@/routes/success/Success';
import { Version } from '@/routes/version';
import { store } from '@/store';
import { assertEnvVars } from '@/utils/assertEnvVars';
import { getTokensRef } from '@/utils/auth';
import { silenceDomMutationErrors } from '@/utils/dom';
import {
  initializeGTM,
  pushPreContainerPageData,
  setDefaultConsent,
} from '@/utils/gtm';
import { getPageType, getSiteEnvironment } from '@/utils/gtmPayloads';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import { initializeSentry } from '@/utils/sentry';
import { getTenantConfiguration } from '@/utils/tenant';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';

import JourneyChangeCheckout from '@/components/journey-change/checkout/JourneyChangeCheckout';
import JourneyChangeOfferSelect from '@/components/journey-change/JourneyChangeOfferSelect';
import '@/i18n';
import AccountDetails from '@/routes/account/accountDetails/AccountDetails';
import AccountSettings from '@/routes/account/accountDetails/AccountSettings';
import Loyalty from '@/routes/account/accountDetails/Loyalty';
import MyInformation from '@/routes/account/accountDetails/MyInformation';
import ChangePassword from '@/routes/account/changePassword/ChangePassword';
import TicketsList from '@/routes/account/tickets/TicketsList';
import TravelPassesList from '@/routes/account/travelPasses/TravelPassesList';
import Vouchers from '@/routes/account/Vouchers';
import VouchersList from '@/routes/account/vouchers/VouchersList';
import JourneyChange from '@/routes/journey-change';
import JourneySelect from '@/routes/journey-change/JourneySelect';
import Refund from '@/routes/refund';
import RefundOverview from '@/routes/refund/RefundOverview';
import '@/styles/index.css';
import '@/styles/toastify.css';
import '@packages/webshop-search-widget/style.css';
import 'react-toastify/dist/ReactToastify.css';

initializeSentry();
assertEnvVars();
silenceDomMutationErrors();

const initializeGoogleTagManager = () => {
  const tenantConfig = getTenantConfiguration();
  const gtmContainerId = tenantConfig.gtmContainerId;

  if (tenantConfig.enableGoogleTagManager && gtmContainerId) {
    // Set the safe consent default BEFORE GTM loads. Cookiebot then sends
    // consent updates once the user makes a choice. Only for tenants whose
    // Cookiebot is wired for Consent Mode v2 (see enableConsentMode).
    if (tenantConfig.enableConsentMode) {
      setDefaultConsent();
    }
    // Tagging plan (BR-57983): page/user context must be on the dataLayer
    // before the GTM container script loads. User identity fields
    // (email_hashed, user_id, ...) are unknown until the profile loads and
    // ride on the login/virtual_page_view events instead.
    pushPreContainerPageData({
      page_type: getPageType(window.location.pathname),
      page_language: i18n.language,
      site_environment: getSiteEnvironment(),
      user_is_logged: Boolean(getTokensRef()),
    });
    initializeGTM(gtmContainerId);
  }
};

initializeGoogleTagManager();

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <NavigateWithUrlSearchParams to="/purchase" /> },
      {
        path: 'purchase',
        element: <Purchase />,
        children: [
          { path: 'outbound/journey', element: <OutboundJourneyList /> },
          { path: 'outbound/offer', element: <OutboundJourneyOfferList /> },
          { path: 'inbound/journey', element: <InboundJourneyList /> },
          { path: 'inbound/offer', element: <InboundJourneyOfferList /> },
          { path: 'non-trip/offer', element: <NonTripOfferList /> },
          { path: 'checkout', element: <Checkout /> },
          { path: '*', element: <Navigate to="/purchase" replace /> },
        ],
      },
      {
        path: 'success',
        element: <Success />,
      },
      // TARA callback runs pre-session and must be reachable in production
      // (not behind an auth guard, not gated to local dev). See BR-56868.
      {
        path: 'auth/callback',
        element: <TaraCallback />,
      },
      {
        path: 'signup/verify',
        element: <SignupVerify />,
      },
      {
        path: 'manage-booking',
        element: <ManageBookingLayout />,
        children: [
          {
            index: true,
            element: <BookingSearch />,
          },
          { path: 'details', element: <BookingDetails /> },
          {
            path: 'edit-passengers',
            element: <EditPassengerDetails />,
          },
          {
            path: '*',
            element: <Navigate to="/manage-booking" replace />,
          },
        ],
      },
      {
        path: 'journey-change',
        element: <JourneyChange />,
        children: [
          {
            index: true,
            element: <JourneySelect />,
          },
          { path: 'offer', element: <JourneyChangeOfferSelect /> },
          { path: 'checkout', element: <JourneyChangeCheckout /> },
          {
            path: '*',
            element: <NavigateWithUrlSearchParams to="/journey-change" />,
          },
        ],
      },
      {
        path: 'refund',
        element: <Refund />,
        children: [
          {
            index: true,
            element: <RefundOverview />,
          },
          { path: '*', element: <NavigateWithUrlSearchParams to="/refund" /> },
        ],
      },
      ...(getTenantConfiguration().showLogin || isLocalDevelopment()
        ? [
            {
              path: 'login',
              children: [
                { index: true, element: <Login /> },
                { path: '*', element: <Navigate to="/login" replace /> },
              ],
            },
            {
              path: 'reset-password',
              children: [
                { index: true, element: <PasswordResetLinkForm /> },
                {
                  path: ':passwordResetHash',
                  element: <NewPasswordFormWithGuard />,
                },
                {
                  path: '*',
                  element: <Navigate to="/reset-password" replace />,
                },
              ],
            },

            {
              path: 'signup',
              children: [
                { index: true, element: <SignUp /> },
                { path: 'confirmation', element: <SignupConfirmation /> },
                { path: '*', element: <Navigate to="/signup" replace /> },
              ],
            },
            {
              path: 'account',
              element: <Account />,
              children: [
                {
                  index: true,
                  element: <Navigate to="/account/details" replace />,
                },
                {
                  path: 'tickets',
                  element: <Tickets />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to="/account/tickets/upcoming" replace />
                      ),
                    },
                    {
                      path: 'upcoming',
                      element: <TicketsList timeType="active" />,
                    },
                    {
                      path: 'history',
                      element: <TicketsList timeType="history" />,
                    },
                  ],
                },
                {
                  path: 'travel-passes',
                  element: <TravelPasses />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to="/account/travel-passes/active" replace />
                      ),
                    },
                    {
                      path: 'active',
                      element: <TravelPassesList timeType="active" />,
                    },
                    {
                      path: 'history',
                      element: <TravelPassesList timeType="history" />,
                    },
                  ],
                },
                {
                  path: 'vouchers',
                  element: <Vouchers />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to="/account/vouchers/active" replace />
                      ),
                    },
                    {
                      path: 'active',
                      element: <VouchersList timeType="active" />,
                    },
                    {
                      path: 'history',
                      element: <VouchersList timeType="history" />,
                    },
                  ],
                },
                {
                  path: 'details',
                  element: <AccountDetails />,
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/account/details/info" replace />,
                    },
                    {
                      path: 'info',
                      element: <MyInformation />,
                    },
                    {
                      path: 'settings',
                      element: <AccountSettings />,
                    },
                    {
                      path: 'loyalty',
                      element: <Loyalty />,
                    },
                  ],
                },
                {
                  path: 'change-password',
                  element: <ChangePassword />,
                },
                {
                  path: '*',
                  element: <Navigate to="/account/details" replace />,
                },
              ],
            },
          ]
        : []),
      {
        path: 'version',
        element: <Version />,
      },
      { path: '*', element: <NavigateWithUrlSearchParams to="/purchase" /> },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<span />}>
    <Provider store={store}>
      <RouterProvider router={router} />
    </Provider>
  </Suspense>
);
